import {useMemo, useState, useCallback, ComponentType} from 'react';
import dynamic from 'next/dynamic';
import {useReports} from '@sail/observability';
import {
  NotificationsAPIResponse,
  useNotifications,
} from 'mkt-ssr/lib/notifications/useNotifications';
import {
  AnyNotification,
  NotificationComponentProps,
  NotificationId,
} from 'mkt-ssr/lib/notifications/types';
import styles from 'mkt-ssr/lib/notifications/Notifications/Notifications.module.css';

type ComponentForNotificationId<K, T> = T extends AnyNotification
  ? K extends NotificationId<T>
    ? ComponentType<React.PropsWithChildren<NotificationComponentProps<T>>>
    : never
  : never;

const ALL_NOTIFICATIONS: {
  [K in NotificationId<AnyNotification>]: ComponentForNotificationId<
    K,
    AnyNotification
  >;
} = {
  MismatchedCountryDetectorNotification: dynamic(
    () => import('../MismatchedCountry/MismatchedCountry'),
  ),
  CookieSettingsNotification: dynamic(() => import('../CookieSettings')),
} as const;

export function NotificationsContainer({
  notifications,
}: {
  notifications: Array<AnyNotification>;
}) {
  const {error} = useReports();

  const [closedNotifications, setClosedNotifications] = useState<Array<string>>(
    [],
  );

  const handleClosed = useCallback((id: string) => {
    setClosedNotifications((v) => [...v, id]);
  }, []);

  const activeNotifications: NotificationsAPIResponse = useMemo(
    () => notifications.filter(({id}) => !closedNotifications.includes(id)),
    [notifications, closedNotifications],
  );

  const components = useMemo(() => {
    const mappedComponents = [];
    for (const {id, data} of activeNotifications) {
      // NB: We cast this down here to just avoid some really complex
      // typing issues that come up when trying to deal with union types.
      // The type signature on the ALL_NOTIFIACTIONS object ensures
      // that these always map the right way so this is still safe.
      const NotificationComponent: ComponentType<
        React.PropsWithChildren<Record<string, unknown>>
      > = ALL_NOTIFICATIONS[id] as ComponentType<
        React.PropsWithChildren<Record<string, unknown>>
      >;
      if (NotificationComponent) {
        mappedComponents.push(
          <NotificationComponent
            key={id}
            {...data}
            id={id}
            onClose={handleClosed}
          />,
        );
      } else {
        error(`A rendering for notification with id "${id}" was not found!`);
      }
    }
    return mappedComponents;
  }, [activeNotifications, error, handleClosed]);

  if (components.length > 0) {
    return (
      <div
        className={styles.NotificationCenter}
        data-testid="notifications-container"
      >
        <div className={styles.NotificationCenter__List}>{components}</div>
      </div>
    );
  } else {
    return null;
  }
}

export function Notifications() {
  const notifications = useNotifications();
  return <NotificationsContainer notifications={notifications} />;
}
